import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Search,
  Download,
  Sparkles,
  Lock,
  HeartHandshake,
  MessageSquare,
  ListChecks,
  Brain,
  Calendar,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import BuyNowButton from "@/components/BuyNowButton";

const freeResources = [
  {
    icon: ListChecks,
    title: "The Weekly 15-Minute School Reset",
    desc: "A simple weekly routine that helps parents catch important school events before they become surprises.",
    file: "/downloads/weekly-reset.pdf",
  },
  {
    icon: MessageSquare,
    title: "School Communication Quick Guide",
    desc: "A simple framework for managing school emails, apps, and announcements.",
    file: "/downloads/school-communication-guide.pdf",
  },
];

const comingSoon = [
  { icon: Brain, title: "Executive Function Toolkit for Parents", desc: "Externalize what your brain shouldn't carry." },
  { icon: Calendar, title: "Family Operations Manual", desc: "The single source of truth for your household." },
  { icon: Sparkles, title: "ADHD-Friendly Family Systems", desc: "Low-friction structure for neurodivergent families." },
  { icon: HeartHandshake, title: "Parent Reset Membership", desc: "Monthly resets, templates, and community." },
];

export default function ResourcesPage() {
  const [search, setSearch] = useState("");
  const [sub, setSub] = useState({ first_name: "", email: "" });
  const [subLoading, setSubLoading] = useState(false);
  const [subDone, setSubDone] = useState(false);
  const [waitLoading, setWaitLoading] = useState(false);

  const filtered = (list) =>
    list.filter(
      (r) =>
        !search.trim() ||
        r.title.toLowerCase().includes(search.toLowerCase()) ||
        r.desc.toLowerCase().includes(search.toLowerCase())
    );

  const subscribe = async (e) => {
    e.preventDefault();
    if (!sub.first_name.trim() || !sub.email.trim()) {
      toast.error("First name and email are required.");
      return;
    }
    setSubLoading(true);
    try {
      await api.post("/subscribers", { ...sub, source: "resources-page" });
      setSubDone(true);
      toast.success("Subscribed! Look out for tips every week.");
      setSub({ first_name: "", email: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubLoading(false);
    }
  };

  const joinCoachingWaitlist = async () => {
    const email = prompt("Enter your email to join the coaching waitlist:");
    const first_name = email ? prompt("And your first name?") : null;
    if (!email || !first_name) return;
    setWaitLoading(true);
    try {
      await api.post("/waitlist", { first_name, email, interest: "coaching" });
      toast.success("Added to the coaching waitlist. We'll be in touch.");
    } catch {
      toast.error("Something went wrong.");
    } finally {
      setWaitLoading(false);
    }
  };

  return (
    <div data-testid="resources-page">
      <Seo
        title="Parent Resources"
        description="Practical tools, checklists, planners, and systems designed to make elementary school parenting easier. Free downloads, paid systems, and coming-soon coaching."
        path="/resources"
      />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.22em] font-bold text-brand-sage">Library</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter text-brand-ink">
            Parent Resources
          </h1>
          <p className="mt-5 text-brand-inkSoft text-base sm:text-lg leading-relaxed">
            Practical tools, checklists, planners, and systems designed to make elementary
            school parenting easier.
          </p>
        </div>

        {/* Search */}
        <div className="mt-10 relative max-w-xl">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-inkMuted" strokeWidth={1.75} />
          <input
            data-testid="resources-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            type="text"
            placeholder="Search resources, checklists, systems..."
            className="w-full bg-white border border-brand-border rounded-full pl-11 pr-5 py-3.5 text-brand-ink placeholder:text-brand-inkMuted outline-none focus:border-brand-sage transition-colors"
          />
        </div>

        {/* Tabs */}
        <Tabs defaultValue="free" className="mt-10">
          <TabsList data-testid="resources-tabs" className="bg-white border border-brand-border rounded-full p-1 h-auto inline-flex">
            <TabsTrigger value="free" data-testid="tab-free" className="rounded-full px-5 py-2 text-sm data-[state=active]:bg-brand-sage data-[state=active]:text-white">
              Free
            </TabsTrigger>
            <TabsTrigger value="paid" data-testid="tab-paid" className="rounded-full px-5 py-2 text-sm data-[state=active]:bg-brand-sage data-[state=active]:text-white">
              Paid
            </TabsTrigger>
            <TabsTrigger value="soon" data-testid="tab-soon" className="rounded-full px-5 py-2 text-sm data-[state=active]:bg-brand-sage data-[state=active]:text-white">
              Coming Soon
            </TabsTrigger>
          </TabsList>

          <TabsContent value="free" className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
              {filtered(freeResources).map((r, i) => (
                <motion.div
                  key={r.title}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  data-testid={`free-resource-${i}`}
                  className="bg-white rounded-2xl border border-brand-border p-7 hover:shadow-soft transition-all"
                >
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-sage/10 text-brand-sage">
                    <r.icon className="h-6 w-6" strokeWidth={1.75} />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-medium text-brand-ink">{r.title}</h3>
                  <p className="mt-2 text-brand-inkSoft text-sm leading-relaxed">{r.desc}</p>
                  <a
                    href="#subscribe"
                    data-testid={`free-get-${i}`}
                    className="mt-6 inline-flex items-center gap-2 bg-brand-sage text-white hover:bg-brand-sageDark transition-colors px-5 py-2.5 rounded-full text-sm font-medium"
                  >
                    <Download className="h-4 w-4" /> Get Free Download
                  </a>
                </motion.div>
              ))}
              {filtered(freeResources).length === 0 && (
                <p className="text-brand-inkMuted text-sm">No matching free resources.</p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="paid" className="mt-8">
            <div className="bg-white rounded-3xl border border-brand-border p-8 md:p-10">
              <div className="flex flex-col md:flex-row gap-8 items-start">
                <div className="flex-1">
                  <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-brand-sage">
                    <Sparkles className="h-3.5 w-3.5" /> Start here
                  </span>
                  <h3 className="mt-3 font-display text-2xl sm:text-3xl font-medium text-brand-ink">
                    The I Missed Picture Day System
                  </h3>
                  <p className="mt-3 text-brand-inkSoft leading-relaxed">
                    A step-by-step system for organizing school communication, tracking
                    events, and reducing parent overwhelm. Includes 7 tools that fit together.
                  </p>
                  <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-brand-ink">
                    {[
                      "School Information Dashboard",
                      "Family Calendar Setup Checklist",
                      "School App Organization Planner",
                      "Teacher Contact Tracker",
                      "Volunteer & Fundraiser Tracker",
                      "School Year Command Center",
                      "Parent Radar Check",
                    ].map((x) => (
                      <li key={x} className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 mt-0.5 text-brand-sage shrink-0" />
                        {x}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/products"
                    data-testid="paid-cta-details"
                    className="mt-7 inline-flex items-center gap-2 bg-white text-brand-sageDark border border-brand-sage hover:bg-brand-bg transition-colors px-6 py-3 rounded-full font-medium"
                  >
                    See full details <ArrowRight className="h-4 w-4" />
                  </Link>
                  <div className="mt-3 sm:mt-7 sm:ml-3 inline-block">
                    <BuyNowButton testid="paid-buy-now" />
                  </div>
                </div>
                <div className="w-full md:w-64 shrink-0">
                  <div className="rounded-2xl bg-brand-bg border border-brand-border p-6 text-center">
                    <p className="text-xs uppercase tracking-wider text-brand-inkMuted">Founding price</p>
                    <p className="mt-2 font-display text-4xl font-semibold text-brand-ink">$29</p>
                    <p className="text-xs text-brand-inkMuted mt-1">one-time · lifetime updates</p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="soon" className="mt-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
              {comingSoon.map((c, i) => (
                <div
                  key={c.title}
                  data-testid={`coming-soon-${i}`}
                  className="bg-white rounded-2xl border border-brand-border p-7"
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-sage/10 text-brand-sage">
                      <c.icon className="h-6 w-6" strokeWidth={1.75} />
                    </span>
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-brand-accent bg-brand-accent/10 px-2.5 py-1 rounded-full">
                      <Lock className="h-3 w-3" /> Coming soon
                    </span>
                  </div>
                  <h3 className="mt-5 font-display text-xl font-medium text-brand-ink">{c.title}</h3>
                  <p className="mt-2 text-brand-inkSoft text-sm leading-relaxed">{c.desc}</p>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Blog Resource Library link */}
        <div className="mt-20 rounded-3xl bg-white border border-brand-border p-8 md:p-10 flex flex-col md:flex-row gap-6 md:items-center md:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] font-bold text-brand-sage">Reading library</p>
            <h3 className="mt-2 font-display text-2xl font-medium text-brand-ink">
              Browse the blog by category
            </h3>
            <p className="mt-2 text-brand-inkSoft text-sm">
              School Organization · Parent Systems · Executive Function · Family Planning · Elementary Tips
            </p>
          </div>
          <Link
            to="/blog"
            data-testid="resources-to-blog"
            className="inline-flex items-center gap-2 bg-brand-sage text-white hover:bg-brand-sageDark transition-colors px-6 py-3 rounded-full font-medium self-start"
          >
            Read the blog <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Subscribe */}
      <section id="subscribe" className="px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-5xl mx-auto rounded-3xl bg-gradient-to-br from-brand-sage to-brand-sageDark text-white p-10 md:p-14 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] font-bold text-white/80">Stay ahead</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-medium tracking-tight">
              Get One Step Ahead parent tips every week
            </h2>
            <p className="mt-4 text-white/90 leading-relaxed">
              Practical school organization strategies, reminders, and parent systems,
              delivered to your inbox.
            </p>
          </div>
          {subDone ? (
            <div data-testid="subscribe-success" className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/15">
              <CheckCircle2 className="h-8 w-8" />
              <p className="mt-3 font-display text-xl">You're subscribed. See you in your inbox.</p>
            </div>
          ) : (
            <form onSubmit={subscribe} className="space-y-3" data-testid="subscribe-form">
              <input
                data-testid="subscribe-name"
                required
                type="text"
                placeholder="First name"
                value={sub.first_name}
                onChange={(e) => setSub({ ...sub, first_name: e.target.value })}
                className="w-full bg-white/95 text-brand-ink placeholder:text-brand-inkMuted rounded-full px-5 py-3 outline-none"
              />
              <input
                data-testid="subscribe-email"
                required
                type="email"
                placeholder="Email address"
                value={sub.email}
                onChange={(e) => setSub({ ...sub, email: e.target.value })}
                className="w-full bg-white/95 text-brand-ink placeholder:text-brand-inkMuted rounded-full px-5 py-3 outline-none"
              />
              <button
                data-testid="subscribe-submit"
                disabled={subLoading}
                type="submit"
                className="w-full bg-white text-brand-sageDark hover:bg-brand-bg transition-colors px-6 py-3.5 rounded-full font-semibold disabled:opacity-70"
              >
                {subLoading ? "Subscribing..." : "Subscribe"}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Future coaching */}
      <section className="px-4 sm:px-6 lg:px-8 pb-24">
        <div className="max-w-5xl mx-auto rounded-3xl border border-brand-border bg-white p-10 md:p-14 text-center">
          <p className="text-xs uppercase tracking-[0.22em] font-bold text-brand-sage">Coming soon</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-medium tracking-tight text-brand-ink">
            Need personalized support?
          </h2>
          <p className="mt-4 text-brand-inkSoft max-w-2xl mx-auto">
            Coming soon: Parent Systems Coaching and Executive Function Coaching, one-on-one
            sessions designed to build a system that fits your family.
          </p>
          <button
            data-testid="coaching-waitlist-cta"
            disabled={waitLoading}
            onClick={joinCoachingWaitlist}
            className="mt-7 inline-flex items-center gap-2 bg-brand-ink text-white hover:bg-brand-sageDark transition-colors px-7 py-3.5 rounded-full font-medium disabled:opacity-70"
          >
            Join the waitlist <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>
    </div>
  );
}
